import { helperPathname, pathname } from "#src/config/pathname";
import i18next from "i18next";
import { Award03, GitCommit, Grid01, Rows01 } from "@untitled-ui/icons-react";
import { ReactComponent as BugLine } from "#src/assets/images/icons/bug-line.svg";
import { ReactComponent as DataLeakLine } from "#src/assets/images/icons/data-leak-line.svg";
import { ISidebarProductItem, ISidebarSecondMenuItem } from "../types";
import { ReactNode } from "react";

const sidebarTFunc = i18next.getFixedT(null, "layout", "sidebar.v2");

// In case somebody wants to put the separator to our sidebar again. It used to be there.
const SEPARATOR = "SEPARATOR";

export const sidebarV2Items: {
  key: string;
  getLabel: () => ReactNode;
  products: (ISidebarProductItem | typeof SEPARATOR)[];
}[] = [
  {
    key: "attack-surface-management",
    getLabel: () => sidebarTFunc("headers.attackSurfaceManagement"),
    products: [
      {
        path: helperPathname.ASSETS,
        getLabel: () => sidebarTFunc("assets.label"),
        icon: Rows01,
        children: [
          {
            path: pathname.DOMAINS,
            getLabel: () => sidebarTFunc("assets.domains"),
          },
          {
            path: pathname.IP_ADDRESS,
            getLabel: () => sidebarTFunc("assets.ipAddress"),
          },
          {
            path: pathname.CERTIFICATES,
            getLabel: () => sidebarTFunc("assets.certificates"),
          },
        ],
      },
      {
        path: pathname.VULNERABILITIES,
        getLabel: () => sidebarTFunc("vulnerabilities.label"),
        icon: BugLine,
      },
    ],
  },
  {
    key: "data-protection",
    getLabel: () => sidebarTFunc("headers.dataProtection"),
    products: [
      {
        path: helperPathname.DATA_LEAK,
        getLabel: () => sidebarTFunc("dataLeak.label"),
        icon: DataLeakLine,
        children: [
          {
            path: pathname.DATA_LEAK_OVERVIEW,
            getLabel: () => sidebarTFunc("dataLeak.overview"),
          },
          {
            path: pathname.DATA_LEAK_FOUND_LEAKS,
            getLabel: () => sidebarTFunc("dataLeak.foundLeaks"),
          },
        ],
      },
      {
        path: helperPathname.ENDPOINT,
        getLabel: () => sidebarTFunc("endpoint.label"),
        icon: GitCommit,
        children: [
          {
            path: pathname.ENDPOINT_OVERVIEW,
            getLabel: () => sidebarTFunc("endpoint.overview"),
          },
          {
            path: pathname.ENDPOINT_DEVICES,
            getLabel: () => sidebarTFunc("endpoint.devices.label"),
          },
          {
            path: pathname.ENDPOINT_DIRECTORY_USERS,
            getLabel: () => sidebarTFunc("endpoint.directory.label"),
          },
          {
            path: pathname.ENDPOINT_PASSPORTS,
            getLabel: () => sidebarTFunc("endpoint.passports"),
          },
          {
            path: pathname.ENDPOINT_POLICIES,
            getLabel: () => sidebarTFunc("endpoint.policies"),
          },
          {
            path: pathname.ENDPOINT_THREAT_PREVENTION_WEBSITES,
            getLabel: () => sidebarTFunc("endpoint.threatPrevention.label"),
          },
          {
            path: pathname.ENDPOINT_DLP_BACKUP_SETTINGS,
            getLabel: () => sidebarTFunc("endpoint.dlp.label"),
          },
        ],
      },
    ],
  },
  {
    key: "security-compliance",
    getLabel: () => sidebarTFunc("headers.securityCompliance"),
    products: [
      {
        path: helperPathname.TRUST_CENTER,
        getLabel: () => sidebarTFunc("trustCenter.label"),
        icon: Award03,
        children: [
          {
            path: pathname.TRUST_CENTER_POLICY,
            getLabel: () => sidebarTFunc("trustCenter.policy"),
          },
          {
            path: pathname.TRUST_CENTER_COMPLIANCE,
            getLabel: () => sidebarTFunc("trustCenter.compliance"),
          },
          {
            path: pathname.TRUST_CENTER_CONTROL,
            getLabel: () => sidebarTFunc("trustCenter.control"),
          },
          {
            path: pathname.TRUST_CENTER_DOCUMENT,
            getLabel: () => sidebarTFunc("trustCenter.document"),
          },
          {
            path: pathname.TRUST_CENTER_THIRD_PARTY,
            getLabel: () => sidebarTFunc("trustCenter.thirdPartyService"),
          },
          {
            path: pathname.TRUST_CENTER_FAQ,
            getLabel: () => sidebarTFunc("trustCenter.faq"),
          },
          {
            path: pathname.TRUST_CENTER_UPDATE,
            getLabel: () => sidebarTFunc("trustCenter.update"),
          },
          {
            path: pathname.TRUST_CENTER_CONFIG,
            getLabel: () => sidebarTFunc("trustCenter.configuration"),
          },
        ],
      },
    ],
  },
];

export const sidebarV2SettingItems: ISidebarSecondMenuItem[] = [
  {
    rootPath: helperPathname.ACCOUNT,
    getLabel: () => sidebarTFunc("accountSettings.label"),
    backTo: {
      path: pathname.SOLUTIONS,
      getLabel: () => sidebarTFunc("dashboard"),
    },
    children: [
      {
        path: pathname.ACCOUNT_SETTINGS,
        getLabel: () => sidebarTFunc("accountSettings.personalDetails"),
      },
      {
        path: pathname.NOTIFICATION,
        getLabel: () => sidebarTFunc("accountSettings.notificationSettings"),
      },
    ],
  },
  {
    rootPath: helperPathname.WORKSPACE,
    getLabel: () => sidebarTFunc("workspaceSettings.label"),
    backTo: {
      path: pathname.SOLUTIONS,
      getLabel: () => sidebarTFunc("dashboard"),
    },
    children: [
      {
        path: pathname.WORKSPACE_DETAILS,
        getLabel: () => sidebarTFunc("workspaceSettings.details"),
      },
      {
        path: pathname.WORKSPACE_SECURITY,
        getLabel: () => sidebarTFunc("workspaceSettings.security"),
      },
      {
        path: pathname.WORKSPACE_MEMBERS,
        getLabel: () => sidebarTFunc("workspaceSettings.members"),
      },
      {
        path: helperPathname.WORKSPACE_BILLING,
        redirect: pathname.WORKSPACE_BILLING_SOLUTIONS,
        getLabel: () => sidebarTFunc("workspaceSettings.billing"),
      },
    ],
  },
];

export const sidebarV2BottomHalfItems: {
  key: string;
  getLabel: () => ReactNode;
  products: ISidebarProductItem[];
}[] = [
  {
    key: "ungrouped",
    getLabel: () => null,
    products: [
      {
        path: pathname.SOLUTIONS,
        getLabel: () => sidebarTFunc("allSolutions.label"),
        icon: Grid01,
      },
    ],
  },
];

export const sidebarV2SecondMenuItems: ISidebarSecondMenuItem[] = [
  {
    rootPath: helperPathname.ENDPOINT_DLP,
    getLabel: () => sidebarTFunc("endpoint.dlp.label"),
    backTo: {
      path: pathname.ENDPOINT_OVERVIEW,
      getLabel: () => sidebarTFunc("endpoint.label"),
    },
    children: [
      {
        path: pathname.ENDPOINT_DLP_BACKUP_SETTINGS,
        getLabel: () => sidebarTFunc("endpoint.dlp.backupSettings"),
      },
      {
        path: pathname.ENDPOINT_DLP_DATA_LABELING,
        getLabel: () => sidebarTFunc("endpoint.dlp.dataLabeling"),
      },
    ],
  },
  {
    rootPath: helperPathname.ENDPOINT_DEVICE_DETAIL,
    getLabel: () => sidebarTFunc("endpoint.devices.label"),
    backTo: {
      path: pathname.ENDPOINT_DEVICES,
      getLabel: () => sidebarTFunc("endpoint.label"),
    },
    children: [
      {
        path: pathname.ENDPOINT_DEVICES_OVERVIEW,
        getLabel: () => sidebarTFunc("endpoint.devices.details.overview"),
      },
      {
        path: pathname.ENDPOINT_DEVICES_SOFTWARE,
        getLabel: () => sidebarTFunc("endpoint.devices.details.software"),
      },
      {
        path: pathname.ENDPOINT_DEVICES_POLICIES,
        getLabel: () => sidebarTFunc("endpoint.devices.details.policies"),
      },
      {
        path: pathname.ENDPOINT_DEVICES_ACTIVITIES,
        getLabel: () => sidebarTFunc("endpoint.devices.details.activities"),
      },
      {
        path: pathname.ENDPOINT_DEVICES_DRIVE,
        getLabel: () => sidebarTFunc("endpoint.devices.details.drive"),
      },
    ],
  },
  {
    rootPath: helperPathname.ENDPOINT_THREAT_PREVENTION,
    getLabel: () => sidebarTFunc("endpoint.threatPrevention.label"),
    backTo: {
      path: pathname.ENDPOINT_OVERVIEW,
      getLabel: () => sidebarTFunc("endpoint.label"),
    },
    children: [
      {
        path: pathname.ENDPOINT_THREAT_PREVENTION_WEBSITES,
        getLabel: () => sidebarTFunc("endpoint.threatPrevention.websites"),
      },
      {
        path: pathname.ENDPOINT_THREAT_PREVENTION_IPS,
        getLabel: () => sidebarTFunc("endpoint.threatPrevention.ips"),
      },
      {
        path: pathname.ENDPOINT_THREAT_PREVENTION_PORTS,
        getLabel: () => sidebarTFunc("endpoint.threatPrevention.ports"),
      },
    ],
  },
  {
    rootPath: helperPathname.ENDPOINT_DIRECTORY,
    getLabel: () => sidebarTFunc("endpoint.directory.label"),
    backTo: {
      path: pathname.ENDPOINT_OVERVIEW,
      getLabel: () => sidebarTFunc("endpoint.label"),
    },
    children: [
      {
        path: pathname.ENDPOINT_DIRECTORY_USERS,
        getLabel: () => sidebarTFunc("endpoint.directory.users"),
      },
      {
        path: pathname.ENDPOINT_DIRECTORY_DEPARTMENTS,
        getLabel: () => sidebarTFunc("endpoint.directory.departments"),
      },
    ],
  },
  ...sidebarV2SettingItems,
];

export const collapsedMenuPages: string[] = [pathname.WORKSPACE_BILLING_RENEW];
